'use client';

import { useEffect } from 'react';

const GlobalErrorHandler: React.FC = () => {
  useEffect(() => {
    // Handle uncaught JavaScript errors
    const handleError = (event: ErrorEvent) => {
      console.error('[Global Error Handler] Uncaught JavaScript error:', {
        message: event.message,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error ? {
          name: event.error.name,
          message: event.error.message,
          stack: event.error.stack
        } : null,
        url: window.location.href,
        userAgent: window.navigator.userAgent,
        timestamp: new Date().toISOString()
      });

      // Track error if analytics is available
      if (window.atlyTracker) {
        try {
          window.atlyTracker.trackEvent('javascript_error', {
            error_message: event.message,
            filename: event.filename,
            line_number: event.lineno,
            column_number: event.colno,
            url: window.location.href
          });
        } catch (trackingError) {
          console.error('[Global Error Handler] Failed to track JS error:', trackingError);
        }
      }
    };

    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('[Global Error Handler] Unhandled promise rejection:', {
        reason: event.reason,
        reasonString: String(event.reason),
        stack: event.reason?.stack,
        url: window.location.href,
        userAgent: window.navigator.userAgent,
        timestamp: new Date().toISOString()
      });

      // Track error if analytics is available
      if (window.atlyTracker) {
        try {
          window.atlyTracker.trackEvent('promise_rejection', {
            reason: String(event.reason),
            url: window.location.href
          });
        } catch (trackingError) {
          console.error('[Global Error Handler] Failed to track promise rejection:', trackingError);
        }
      }
    };

    // Add event listeners
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Also log Google Maps specific errors if they occur on the window
    const windowWithGoogleMaps = window as any;
    const originalGoogleError = windowWithGoogleMaps.gm_authFailure;
    windowWithGoogleMaps.gm_authFailure = () => {
      console.error('[Global Error Handler] Google Maps authentication failed - API key may be invalid or restricted');
      if (originalGoogleError) {
        originalGoogleError();
      }
    };

    // Cleanup
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      if (originalGoogleError) {
        windowWithGoogleMaps.gm_authFailure = originalGoogleError;
      }
    };
  }, []);

  return null; // This component doesn't render anything
};

export default GlobalErrorHandler; 